<template>
  <div class="pa-0 secondary--text text--darken-3">
    <!-- Header Area -->
    <div class="bg-grey-light pa-8 border-bottom">
      <div class="d-flex align-center mb-2">
        <v-avatar color="primary" size="40" class="mr-4 shadow-sm">
          <v-icon color="white" small>fas fa-handshake</v-icon>
        </v-avatar>
        <div>
          <div class="text-h5 font-weight-black primary--text lh-1">{{ provider.id > -1 ? 'Editar Proveedor' : 'Nuevo Proveedor' }}</div>
          <div class="caption grey--text font-weight-bold text-uppercase tracking-wider mt-1">Gestión de ficha técnica de proveedor</div>
        </div>
      </div>
    </div>

    <div class="pa-8">
      <ValidationObserver v-slot="{ handleSubmit }">
        <v-form @submit.prevent="handleSubmit(save)">
          
          <!-- Section: Identification -->
          <div class="caption primary--text font-weight-black text-uppercase tracking-widest mb-6 d-flex align-center">
            <v-icon x-small color="primary" class="mr-2">fas fa-id-card</v-icon> Identificación Legal
          </div>

          <v-row dense>
            <v-col cols="12">
              <ValidationProvider name="RUT" rules="required" v-slot="{ errors }">
                <div class="caption grey--text font-weight-black mb-1 ml-1 px-1">RUT Empresa</div>
                <v-text-field
                  v-model="provider.rut"
                  placeholder="12.345.678-9"
                  outlined
                  dense
                  class="modern-input bg-white mb-4"
                  :error-messages="errors"
                  :loading="loading"
                  prepend-inner-icon="fas fa-id-badge"
                  :append-icon="rutValido ? 'fas fa-check-circle success--text' : ''"
                  persistent-hint
                  hint="Formato: 12.345.678-9"
                />
              </ValidationProvider>
            </v-col>
            
            <v-col cols="12">
              <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                <div class="caption grey--text font-weight-black mb-1 ml-1 px-1">Razón Social / Nombre Completo</div>
                <v-text-field
                  v-model="provider.name"
                  placeholder="Nombre de la empresa o proveedor"
                  outlined
                  dense
                  class="modern-input bg-white mb-4"
                  :error-messages="errors"
                  :loading="loading"
                  prepend-inner-icon="fas fa-building"
                />
              </ValidationProvider>
            </v-col>
          </v-row>

          <!-- Section: Commercial Config -->
          <div class="caption primary--text font-weight-black text-uppercase tracking-widest mt-4 mb-6 d-flex align-center">
            <v-icon x-small color="primary" class="mr-2">fas fa-wallet</v-icon> Configuración Comercial
          </div>

          <v-row dense>
            <v-col cols="12">
              <ValidationProvider name="Condición de Pago" rules="required" v-slot="{ errors }">
                <div class="caption grey--text font-weight-black mb-1 ml-1 px-1">Condición de Pago (Días)</div>
                <v-text-field
                  v-model.number="provider.payment"
                  type="number"
                  placeholder="Ej: 30"
                  outlined
                  dense
                  class="modern-input bg-white mb-4"
                  :error-messages="errors"
                  prepend-inner-icon="fas fa-calendar-day"
                  suffix="Días"
                  :min="0"
                />
              </ValidationProvider>
            </v-col>
          </v-row>

          <!-- Info Highlight Box -->
          <v-alert
            outlined
            color="primary"
            class="modern-alert rounded-lg mt-6 pa-4"
            icon="fas fa-info-circle"
          >
            <div class="body-2 font-weight-bold mb-1 primary--text">Nota de Facturación</div>
            <div class="caption opacity-80 grey--text text--darken-3">Los datos se validarán para la generación de órdenes de compra y procesos contables.</div>
          </v-alert>

          <!-- Footer Buttons -->
          <div class="d-flex mt-10 gap-3">
            <v-btn 
              type="submit" 
              color="primary" 
              large 
              depressed 
              :loading="loading"
              class="flex-grow-1 py-6 rounded-lg font-weight-black text-uppercase tracking-widest shadow-md"
            >
              <v-icon small class="mr-2">fas fa-save</v-icon>
              {{ provider.id > -1 ? 'Actualizar Proveedor' : 'Registrar Proveedor' }}
            </v-btn>
            <v-btn 
              text 
              large 
              color="grey darken-1"
              class="rounded-lg font-weight-bold px-8"
              @click.prevent="$emit('close')"
            >
              Cancelar
            </v-btn>
          </div>
        </v-form>
      </ValidationObserver>
    </div>
  </div>
</template>

<script>
import { getProvider, createProvider, updateProvider } from '@/api/stocks'
import notifications from '@/utils/notifications'
import { validaRUT } from '@/utils/rut_validator'

export default {
  name: 'ProviderCreateUpdate',
  props: {
    id: { required: true }
  },
  data() {
    return {
      provider: {
        id: -1,
        rut: '',
        name: '',
        payment: 0
      },
      loading: false,
    }
  },
  methods: {
    async save() {
      if (this.provider.rut && !this.rutValido) {
        notifications.showError('El RUT ingresado no es válido')
        return
      }
      
      this.loading = true
      const payload = {
        rut: this.provider.rut,
        name: this.provider.name,
        payment: this.provider.payment
      }

      try {
        if (this.id > -1) {
          await updateProvider(this.id, payload)
          notifications.showSuccess('Ficha de proveedor actualizada')
        } else {
          const res = await createProvider(payload)
          notifications.showSuccess('Proveedor registrado exitosamente')
          this.$emit('updated', res)
        }
        this.$emit('updated')
        this.$emit('close')
      } catch (err) {
        notifications.showError('Error al procesar: ' + err.message)
      } finally {
        this.loading = false
      }
    },
    async get() {
      if (this.id > -1) {
        this.loading = true
        try {
          const res = await getProvider(this.id)
          this.provider = res
        } catch (err) {
          notifications.showError('Error al cargar datos')
        } finally {
          this.loading = false
        }
      } else {
        this.provider = {
          id: -1,
          rut: '',
          name: '',
          payment: 0
        }
      }
    }
  },
  computed: {
    rutValido() {
      return this.provider.rut ? validaRUT(this.provider.rut) : false
    },
  },
  watch: {
    id: { handler: 'get', immediate: true }
  }
}
</script>

<style scoped>
.bg-grey-light { background-color: #f8f9fa; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.lh-1 { line-height: 1 !important; }
.tracking-widest { letter-spacing: 2px; }
.tracking-wider { letter-spacing: 1px; }
.gap-3 { gap: 12px; }
.opacity-80 { opacity: 0.8; }

.modern-input >>> .v-input__control { min-height: 48px !important; }
.modern-input >>> fieldset { border: 1px solid rgba(0,0,0,0.1) !important; border-radius: 8px !important; }
.modern-input >>> .v-input__prepend-inner { margin-top: 12px !important; margin-right: 12px !important; }

.shadow-md { box-shadow: 0 4px 12px rgba(var(--v-primary-base), 0.2) !important; }
.shadow-sm { box-shadow: 0 2px 4px rgba(0,0,0,0.05) !important; }

.modern-alert { border-width: 1px !important; background-color: rgba(var(--v-primary-base), 0.02) !important; }
</style>